/**
 * 统一邮件API模块
 * 符合重构设计原则：邮件相关的所有操作统一在邮件模块中处理
 */

import apiClient from "./axios";

/**
 * 获取邮件详细内容（统一端点）
 * @param {number} emailId - 邮件ID
 * @param {string} [businessContext] - 业务上下文验证，格式：{record_type}:{record_id}，如 SCHEDULE:123
 * @returns {Promise} API响应
 */
export const getEmailContent = async (emailId, businessContext = null) => {
    try {
        const params = {};
        if (businessContext) {
            params.business_context = businessContext;
        }

        const response = await apiClient.get(`/v1/mail/emails/${emailId}/content`, { params });
        return response;
    } catch (error) {
        console.error("获取邮件内容失败:", error);
        throw error;
    }
};

/**
 * 获取邮件完整状态信息（包含内容和发送状态）
 * @param {number} emailId - 邮件ID
 * @param {boolean} [includeRecipientDetails=false] - 是否包含收件人详细状态
 * @returns {Promise} API响应
 */
export const getEmailFullStatus = async (emailId, includeRecipientDetails = false) => {
    try {
        const params = {
            include_recipient_details: includeRecipientDetails,
        };

        const response = await apiClient.get(`/v1/mail/emails/${emailId}/full-status`, { params });
        return response;
    } catch (error) {
        console.error("获取邮件完整状态失败:", error);
        throw error;
    }
};

/**
 * 获取计划关联邮件内容
 * 内部会调用统一的邮件内容端点，并进行业务上下文验证
 * @param {number} scheduleId - 计划ID
 * @param {number} emailId - 邮件ID
 * @returns {Promise} API响应
 */
export const getScheduleEmailContent = async (scheduleId, emailId) => {
    try {
        // 使用业务上下文验证确保邮件与计划的关联关系
        const businessContext = `SCHEDULE:${scheduleId}`;
        return await getEmailContent(emailId, businessContext);
    } catch (error) {
        console.error("获取计划关联邮件内容失败:", error);
        throw error;
    }
};

/**
 * 获取航班关联邮件内容
 * 内部会调用统一的邮件内容端点，并进行业务上下文验证
 * @param {number} flightId - 航班ID
 * @param {number} emailId - 邮件ID
 * @returns {Promise} API响应
 */
export const getFlightEmailContent = async (flightId, emailId) => {
    try {
        // 使用业务上下文验证确保邮件与航班的关联关系
        const businessContext = `FLIGHT_OPERATION:${flightId}`;
        return await getEmailContent(emailId, businessContext);
    } catch (error) {
        console.error("获取航班关联邮件内容失败:", error);
        throw error;
    }
};

/**
 * 获取预设发件账号列表
 * 返回用户可用的预设发件账号（不含敏感信息）
 * @returns {Promise} API响应，包含 { success: true, data: [{ key, email, display_name, description }] }
 */
export const getPresetSenderAccounts = async () => {
    try {
        const response = await apiClient.get("/v1/mail/preset-sender-accounts");
        return response.data;
    } catch (error) {
        console.error("获取预设发件账号列表失败:", error);
        throw error;
    }
};

export default {
    getEmailContent,
    getEmailFullStatus,
    getScheduleEmailContent,
    getFlightEmailContent,
    getPresetSenderAccounts,
};
